import BasisSwitcher from "@app/components/basis-switcher";
import DateRangeSwitcher from "@app/components/date-range-switcher";
import PerspectiveSwitcher from "@app/components/perspective-switcher";
import QueueFooter from "@app/components/account-queue/queue-footer";
import AccessGate from "@app/pages/access/access-gate";
import { getAuthToken, clearAuthToken } from "@app/relay/fetch-relay";
import {
  Link,
  Outlet,
  createFileRoute,
  redirect,
} from "@tanstack/react-router";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";

export const Route = createFileRoute("/_authed")({
  beforeLoad: ({ location }) => {
    if (!getAuthToken()) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
  },
  component: AuthedLayout,
});

const navLinkClass =
  "block px-3 py-2 rounded text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 [&.active]:bg-gray-100 [&.active]:text-gray-900";

function AuthedLayout() {
  const logout = () => {
    clearAuthToken();
    window.location.assign("/login");
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      <aside className="w-56 shrink-0 h-screen sticky top-0 bg-white border-r border-gray-200 flex flex-col">
        <div className="h-14 shrink-0 flex items-center gap-2 px-4 font-bold border-b border-gray-200">
          QBO Sync
          <span
            title={`Deployed commit ${__APP_COMMIT__}`}
            className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] font-normal text-gray-400"
          >
            {__APP_COMMIT__.slice(0, 7)}
          </span>
        </div>

        <nav className="flex-1 overflow-y-auto p-2 flex flex-col gap-0.5">
          <Link to="/dashboard" className={navLinkClass}>
            Dashboard
          </Link>
          <Link to="/cash-position" className={navLinkClass}>
            Cash position
          </Link>
          <Link to="/pnl" className={navLinkClass}>
            P&L
          </Link>
          <Link to="/exports" className={navLinkClass}>
            Exports
          </Link>
          <Link to="/revenue" className={navLinkClass}>
            Revenue
          </Link>
          <Link to="/expense-natures" className={navLinkClass}>
            By nature
          </Link>
          <Link to="/books" className={navLinkClass}>
            Books
          </Link>
          <Link to="/loans" className={navLinkClass}>
            Loans
          </Link>
          <Link to="/dividends" className={navLinkClass}>
            Dividends
          </Link>
          <Link to="/mrr" className={navLinkClass}>
            MRR
          </Link>
          <Link to="/income-curves" className={navLinkClass}>
            Income curves
          </Link>
          <Link to="/churn" className={navLinkClass}>
            Churn & Retention
          </Link>
          <Link to="/explore" className={navLinkClass}>
            Explore
          </Link>
          <Link to="/cac" className={navLinkClass}>
            Fully Loaded CAC
          </Link>
          <Link to="/stripe" className={navLinkClass}>
            Stripe
          </Link>
          <Link to="/review" className={navLinkClass}>
            Review
          </Link>
          <Link to="/needs-review" className={navLinkClass}>
            Needs review
          </Link>
          {/* U15: Sources is the control room; the /statements registry is its
              drill-in (linked from the page header and each account row). */}
          <Link to="/sources" className={navLinkClass}>
            Sources
          </Link>
          <div className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Wallets
          </div>
          <Link
            to="/wallet/$slug"
            params={{ slug: "deel" }}
            className={navLinkClass}
          >
            Deel
          </Link>
          <Link
            to="/wallet/$slug"
            params={{ slug: "wise" }}
            className={navLinkClass}
          >
            Wise
          </Link>
          <Link to="/transactions" className={navLinkClass}>
            Transactions
          </Link>
          <Link to="/stakeholders" className={navLinkClass}>
            Stakeholders
          </Link>
          <Link to="/stakeholders-cat" className={navLinkClass}>
            Stakeholders by cost
          </Link>
          <Link to="/stakeholders-merge" className={navLinkClass}>
            Merge stakeholders
          </Link>
          <Link to="/data-health" className={navLinkClass}>
            Data health
          </Link>
          <Link to="/queue" className={navLinkClass}>
            Queue
          </Link>
          <Link to="/board" className={navLinkClass}>
            Board
          </Link>
          <Link to="/diligence" className={navLinkClass}>
            Diligence
          </Link>
          <Link to="/categorize" className={navLinkClass}>
            Categorize
          </Link>
          <Link to="/categorization-overview" className={navLinkClass}>
            Categorization overview
          </Link>
          <Link to="/budgets" className={navLinkClass}>
            Budgets
          </Link>
          <Link to="/rules" className={navLinkClass}>
            Rules
          </Link>
          <Link to="/fact-sheet" className={navLinkClass}>
            Public fact sheet
          </Link>
        </nav>

        <div className="shrink-0 border-t border-gray-200 p-3 flex flex-col gap-2">
          <DateRangeSwitcher />
          <div className="text-xs text-gray-400">Basis</div>
          <BasisSwitcher />
          <PerspectiveSwitcher />
          <a
            href="mailto:engineering@zenmaid.com?subject=QBO%20Sync%20support"
            className="text-left text-sm text-gray-500 hover:text-gray-900"
          >
            Support
          </a>
          <button
            type="button"
            onClick={logout}
            className="text-left text-sm text-gray-500 hover:text-gray-900"
          >
            Log out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <main>
          <AccessGate>
            <Outlet />
          </AccessGate>
        </main>
      </div>

      {/* Every page carries this. It renders null when the queue is empty, and
          its own Suspense boundary keeps a slow queue read from blocking the
          page the person actually came for.

          The ErrorBoundary is not belt-and-braces: this sits INSIDE the layout
          every authenticated page renders through, so anything it throws takes
          down whatever the person actually came here to do. A queue indicator is
          never worth that — if it cannot read the queue, it shows nothing. */}
      <ErrorBoundary fallback={null}>
        <Suspense fallback={null}>
          <QueueFooter />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}
